import { Box, BoxComponentProps, Grid, List } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { TextWrapper } from "@/components/TextWrapper";
import { PhysicalExamDisplays } from "@/fhir/enums/physical-exams";

import { NewbornPostpartumPhysicalExam } from "./zod-schemas/newborn-postpartum-physical-exam";
import { PhysicalExamForm } from "./zod-schemas/physical-exams";

interface PhysicalExamSectionViewerProps extends BoxComponentProps {
  form: UseFormReturnType<PhysicalExamForm | NewbornPostpartumPhysicalExam>;
  title: string;
  field: string;
  isDeclined: boolean;
}

interface PhysicalExamItemViewerProps extends BoxComponentProps {
  label: string;
  details: string[];
}

export default function PhysicalExamSectionViewer({
  form,
  title,
  field,
  isDeclined,
  ...props
}: PhysicalExamSectionViewerProps) {
  const values = form.values.sections[field] as PhysicalExamForm["sections"][string];

  const isSingleChoice = "singleChoice" in values;

  if (isSingleChoice) {
    const singleChoiceValues = values as {
      singleChoice: boolean;
      singleChoiceDetails: string[];
      declined: boolean;
    };

    const isUnanswered = !isDeclined && singleChoiceValues.singleChoice === undefined;
    if (isUnanswered) return null;

    const singleChoiceLabel = singleChoiceValues.singleChoice ? "Yes" : "No";

    return (
      <Box aria-label={`${title} View`} {...props}>
        <Grid justify="flex-start">
          <Grid.Col span={{ base: 12, sm: 2 }}>
            <TextWrapper variant="bold">{title}</TextWrapper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 10 }}>
            {singleChoiceValues.singleChoice !== undefined && (
              <PhysicalExamItemViewer
                label={singleChoiceLabel}
                details={singleChoiceValues.singleChoiceDetails}
              />
            )}
            {isDeclined && <TextWrapper c="gray.6">Declined</TextWrapper>}
          </Grid.Col>
        </Grid>
      </Box>
    );
  }

  // Original logic for multiple choice items
  const regularValues = values as {
    normal?: boolean;
    notNormal?: boolean;
    normalDetails: string[];
    notNormalDetails: string[];
    notes?: string;
  };
  const hasNotes = Boolean(regularValues.notes && regularValues.notes.trim());
  const isUnanswered =
    !isDeclined && !regularValues.normal && !regularValues.notNormal && !hasNotes;
  if (isUnanswered) return null;

  return (
    <Box aria-label={`${title} View`} {...props}>
      <Grid justify="flex-start">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <TextWrapper variant="bold">{title}</TextWrapper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          {regularValues.normal && (
            <PhysicalExamItemViewer label="Normal findings" details={regularValues.normalDetails} />
          )}
          {regularValues.notNormal && (
            <PhysicalExamItemViewer
              label="Not normal findings"
              mt={regularValues.normal ? "md" : 0}
              details={regularValues.notNormalDetails}
            />
          )}
          {isDeclined && <TextWrapper c="gray.6">Declined</TextWrapper>}
          {/* Render section notes if present */}
          {hasNotes && (
            <Box mt="sm">
              <TextWrapper variant="bold">Notes</TextWrapper>
              <TextWrapper>{regularValues.notes}</TextWrapper>
            </Box>
          )}
        </Grid.Col>
      </Grid>
    </Box>
  );
}

function PhysicalExamItemViewer({ label, details, ...props }: PhysicalExamItemViewerProps) {
  return (
    <Box {...props} aria-label={`${label} View`}>
      <TextWrapper c="gray.6">{label}</TextWrapper>
      <List mt="xs" pl="xs" size="xs" spacing={4}>
        {details.map((detail) => {
          const display = PhysicalExamDisplays[detail as keyof typeof PhysicalExamDisplays];
          return (
            <List.Item key={detail}>
              <TextWrapper>{display}</TextWrapper>
            </List.Item>
          );
        })}
      </List>
    </Box>
  );
}
